import { RefObject, useEffect } from 'react'
import {
  Camera,
  PerspectiveCamera,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from 'three'
import { TrackballControls as TrackballControlsImpl } from 'three-stdlib'
import { useShallow } from 'zustand/react/shallow'

import { CONTROLS, useCADPageStore } from '@/state'

type UseZoomToMouseArgs = {
  camera: Camera
  domElement: HTMLCanvasElement | null
  trackballControlsRef: RefObject<TrackballControlsImpl | null>
  updateCameraState: () => void
}

/**
 * Keeps TrackballControls camera state synchronized with the CAD store and
 * implements wheel zoom that anchors at the mouse cursor.
 *
 * Why this exists:
 * - Three.js controls are imperative objects that mutate camera/target directly.
 * - CAD page state is also persisted in Zustand for reset/view restore behavior.
 * - Default Trackball wheel zoom moves toward viewport center, but the product
 *   requirement is Figma-like "zoom to cursor".
 *
 * Responsibilities:
 * 1) Apply store -> controls/camera updates when external actions (e.g. Reset All,
 *    applying a saved view) change camera/target state in Zustand.
 * 2) Intercept wheel input and run custom zoom geometry around the cursor anchor.
 *
 * Non-responsibilities:
 * - It does not decide when Trackball mode is enabled; it observes `controlsId`
 *   from CAD page state.
 * - It does not own persistence; it only calls `updateCameraState` after zoom.
 */
export const useZoomToMouse = ({
  camera,
  domElement,
  trackballControlsRef,
  updateCameraState,
}: UseZoomToMouseArgs) => {
  const controlsId = useCADPageStore(useShallow((state) => state.controlsId))
  const target = useCADPageStore(useShallow((state) => state.target))
  const storeCameraPosition = useCADPageStore(
    useShallow((state) => state.cameraPosition),
  )
  const storeCameraFov = useCADPageStore(useShallow((state) => state.cameraFov))
  const storeCameraZoom = useCADPageStore(
    useShallow((state) => state.cameraZoom),
  )
  const storeCameraQuaternion = useCADPageStore(
    useShallow((state) => state.cameraQuaternion),
  )
  const storeCameraUp = useCADPageStore(useShallow((state) => state.cameraUp))

  /**
   * Store -> Three sync effect.
   *
   * This intentionally runs only in Trackball mode to avoid fighting other
   * control systems. We perform tolerant comparisons first and mutate only when
   * values actually differ, so we avoid unnecessary projection/control updates.
   */
  useEffect(() => {
    if (controlsId !== CONTROLS.TRACKBALL_CONTROLS) return

    const controls = trackballControlsRef.current
    if (!controls || !(camera instanceof PerspectiveCamera)) return

    // Tracks whether any camera/target field changed in this pass.
    let changed = false

    if (target.length === 3) {
      // `target` is the Trackball orbit pivot; rotating occurs around this point.
      const targetChanged =
        Math.abs(controls.target.x - target[0]) > 1e-9 ||
        Math.abs(controls.target.y - target[1]) > 1e-9 ||
        Math.abs(controls.target.z - target[2]) > 1e-9
      if (targetChanged) {
        controls.target.fromArray(target)
        changed = true
      }
    }

    if (storeCameraPosition.length === 3) {
      // Position controls dolly distance/offset relative to the orbit target.
      const cameraPositionChanged =
        Math.abs(camera.position.x - storeCameraPosition[0]) > 1e-9 ||
        Math.abs(camera.position.y - storeCameraPosition[1]) > 1e-9 ||
        Math.abs(camera.position.z - storeCameraPosition[2]) > 1e-9
      if (cameraPositionChanged) {
        camera.position.fromArray(storeCameraPosition)
        changed = true
      }
    }

    if (storeCameraQuaternion.length === 4) {
      // Quaternion controls orientation (yaw/pitch/roll) in world space.
      const cameraQuaternionChanged =
        Math.abs(camera.quaternion.x - storeCameraQuaternion[0]) > 1e-9 ||
        Math.abs(camera.quaternion.y - storeCameraQuaternion[1]) > 1e-9 ||
        Math.abs(camera.quaternion.z - storeCameraQuaternion[2]) > 1e-9 ||
        Math.abs(camera.quaternion.w - storeCameraQuaternion[3]) > 1e-9
      if (cameraQuaternionChanged) {
        camera.quaternion.fromArray(storeCameraQuaternion)
        changed = true
      }
    }

    if (storeCameraUp.length === 3) {
      // Up-vector affects how controls interpret rotations around vertical axis.
      const cameraUpChanged =
        Math.abs(camera.up.x - storeCameraUp[0]) > 1e-9 ||
        Math.abs(camera.up.y - storeCameraUp[1]) > 1e-9 ||
        Math.abs(camera.up.z - storeCameraUp[2]) > 1e-9
      if (cameraUpChanged) {
        camera.up.fromArray(storeCameraUp)
        changed = true
      }
    }

    if (Math.abs(camera.fov - storeCameraFov) > 1e-9) {
      camera.fov = storeCameraFov
      changed = true
    }

    if (Math.abs(camera.zoom - storeCameraZoom) > 1e-9) {
      camera.zoom = storeCameraZoom
      changed = true
    }

    if (changed) {
      // Any change to fov/zoom/orientation requires projection + control refresh.
      camera.updateProjectionMatrix()
      controls.update()
    }
  }, [
    camera,
    controlsId,
    storeCameraFov,
    storeCameraPosition,
    storeCameraQuaternion,
    storeCameraUp,
    storeCameraZoom,
    target,
    trackballControlsRef,
  ])

  useEffect(() => {
    if (!domElement) return

    // Reused temporaries: avoids allocations in wheel hot-path.
    const mouse = new Vector2()
    const raycaster = new Raycaster()
    const zoomPlane = new Plane()
    const zoomAnchor = new Vector3()
    const viewDirection = new Vector3()
    const nextCameraPosition = new Vector3()
    const nextTarget = new Vector3()

    const onWheel = (event: WheelEvent) => {
      if (controlsId !== CONTROLS.TRACKBALL_CONTROLS) return

      const controls = trackballControlsRef.current
      if (!controls || !(camera instanceof PerspectiveCamera)) return

      const rect = domElement.getBoundingClientRect()
      if (!rect.width || !rect.height) return

      // Prevent browser/document scrolling while zooming the canvas.
      event.preventDefault()

      // Normalize line/page wheel deltas into pixel-ish units for consistent feel.
      const deltaMultiplier =
        event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1
      const normalizedDelta = event.deltaY * deltaMultiplier
      if (!normalizedDelta) return

      // Exponential scaling keeps zoom speed smooth and proportional.
      const zoomFactor = Math.exp((normalizedDelta * controls.zoomSpeed) / 2000)
      const currentDistance = camera.position.distanceTo(controls.target)
      if (currentDistance <= 0) return

      // Respect Trackball min/max distance constraints.
      const minDistance = controls.minDistance ?? 0
      const maxDistance = controls.maxDistance ?? Infinity
      const clampedDistance = Math.min(
        maxDistance,
        Math.max(minDistance, currentDistance * zoomFactor),
      )
      const clampedZoomFactor = clampedDistance / currentDistance
      if (!Number.isFinite(clampedZoomFactor) || clampedZoomFactor === 1) return

      // Convert cursor pixel coordinates to Normalized Device Coordinates.
      mouse.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      )
      raycaster.setFromCamera(mouse, camera)

      // Build a plane through the current orbit target, orthogonal to view dir.
      // Intersecting the cursor ray with this plane gives the world-space zoom
      // anchor that should appear stable under the mouse while dollying.
      camera.getWorldDirection(viewDirection)
      zoomPlane.setFromNormalAndCoplanarPoint(viewDirection, controls.target)
      if (!raycaster.ray.intersectPlane(zoomPlane, zoomAnchor)) return

      // Scale both camera position and orbit target around the same anchor.
      // This preserves local composition at the cursor instead of zooming center.
      nextCameraPosition
        .copy(camera.position)
        .sub(zoomAnchor)
        .multiplyScalar(clampedZoomFactor)
        .add(zoomAnchor)
      nextTarget
        .copy(controls.target)
        .sub(zoomAnchor)
        .multiplyScalar(clampedZoomFactor)
        .add(zoomAnchor)

      camera.position.copy(nextCameraPosition)
      controls.target.copy(nextTarget)
      controls.update()

      // Persist the new camera/target into Zustand so other features (views,
      // reset, screenshot metadata) observe the same state the user sees.
      updateCameraState()
    }

    domElement.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      domElement.removeEventListener('wheel', onWheel)
    }
  }, [camera, controlsId, domElement, trackballControlsRef, updateCameraState])
}
